import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ConsumerLayout from './ConsumerLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { orders as ordersApi } from '../../utils/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Receipt, Package, Truck, MapPin, RefreshCw, Navigation } from 'lucide-react';
import { toast } from 'sonner';
import DeliveryTracker from '../shared/DeliveryTracker';
import LiveOrderStatus from './LiveOrderStatus';

export default function ConsumerOrders({ user, onLogout }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await ordersApi.list();
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      confirmed: 'bg-blue-500',
      preparing: 'bg-purple-500',
      ready: 'bg-green-500',
      dispatched: 'bg-purple-500',
      in_transit: 'bg-orange-500',
      delivered: 'bg-emerald-600',
      completed: 'bg-gray-500',
      cancelled: 'bg-red-500',
    };
    return colors[status] || 'bg-gray-500';
  };

  return (
    <ConsumerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              My Orders
            </h1>
            <p className="text-gray-600 mt-1">View your order history and track deliveries</p>
          </div>
          <Button variant="outline" onClick={loadOrders} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Orders List */}
        {loading ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-500">Loading orders...</p>
            </CardContent>
          </Card>
        ) : orders.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No orders yet</p>
              <p className="text-gray-400 text-sm mt-2">Your order history will appear here</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Card key={order.id} data-testid={`order-${order.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">Order #{order.id.slice(0, 8)}</CardTitle>
                      <CardDescription>{new Date(order.created_at).toLocaleString()}</CardDescription>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={getStatusColor(order.status)} data-testid={`order-status-${order.id}`}>
                        {order.status}
                      </Badge>
                      {/* Live SSE status (Cluster 6 · P7) — overlays realtime badge under polled status. */}
                      {['confirmed', 'preparing', 'ready'].includes(order.status) && (
                        <LiveOrderStatus orderId={order.id} fallbackStatus={order.status} />
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Pickup Code Card — shown only for pickup orders.
                      Delivery orders intentionally omit the code (DoorDash courier
                      handles the handoff via a separate DP-XXXX code). */}
                  {order.delivery_method === 'pickup' && order.pickup_code && (
                    <div
                      className="rounded-2xl p-5 text-center"
                      style={{
                        background: 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 50%, #d1fae5 100%)',
                        border: '2px dashed #10b981',
                      }}
                      data-testid={`pickup-code-card-${order.id}`}
                    >
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-emerald-700">
                        Pickup Code
                      </p>
                      <p
                        className="text-4xl font-extrabold text-emerald-800 my-2 tracking-wider"
                        style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
                        data-testid={`pickup-code-${order.id}`}
                      >
                        {(order.pickup_code_prefix || 'CP')}-{order.pickup_code}
                      </p>
                      <p className="text-xs text-emerald-700 leading-snug">
                        Show this code to the cashier at pickup.
                      </p>
                    </div>
                  )}

                  {/* Retailer Info */}
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <Package className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{order.drlp_name}</p>
                      <p className="text-sm text-gray-500">Retailer</p>
                    </div>
                  </div>

                  {/* Delivery Info */}
                  <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className={`p-2 rounded-lg ${order.delivery_method === 'delivery' ? 'bg-purple-100' : 'bg-emerald-100'}`}>
                      {order.delivery_method === 'delivery' ? (
                        <Truck className={`w-5 h-5 ${order.delivery_method === 'delivery' ? 'text-purple-600' : 'text-emerald-600'}`} />
                      ) : (
                        <MapPin className={`w-5 h-5 ${order.delivery_method === 'delivery' ? 'text-purple-600' : 'text-emerald-600'}`} />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 capitalize">{order.delivery_method}</p>
                      {order.delivery_method === 'delivery' ? (
                        <p className="text-sm text-gray-500">{order.delivery_address}</p>
                      ) : (
                        <p className="text-sm text-gray-500">Pickup at {new Date(order.pickup_time).toLocaleString()}</p>
                      )}
                    </div>
                  </div>

                  {/* Delivery Tracking for active deliveries */}
                  {order.delivery_external_id && order.delivery_method === 'delivery' && (
                    <div className="space-y-3">
                      {/* Track Delivery Button - Links to full page */}
                      <Button 
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        onClick={() => navigate(`/consumer/track/${order.id}`)}
                        data-testid={`track-delivery-btn-${order.id}`}
                      >
                        <Navigation className="w-4 h-4 mr-2" />
                        Track Delivery
                      </Button>
                      
                      {/* Compact inline tracker */}
                      <DeliveryTracker 
                        externalDeliveryId={order.delivery_external_id}
                        orderId={order.id}
                        showCancelButton={false}
                        compact={true}
                      />
                    </div>
                  )}

                  {/* Items — show per-line savings (deal price vs regular price) */}
                  <div className="space-y-2">
                    <p className="font-medium text-sm text-gray-700">Items</p>
                    {order.items.map((item, idx) => {
                      // Coerce every numeric field defensively — an order
                      // fixture with a missing price or quantity would
                      // otherwise cascade NaN into the ``meals`` badge
                      // and trip React's "Received NaN for the `children`
                      // attribute" warning (Jul 19 cleanup).
                      const price = Number(item.price) || 0;
                      const qty = Number(item.quantity) || 0;
                      const reg = Number(item.regular_price) || 0;
                      const dealLineTotal = price * qty;
                      const regLineTotal = reg * qty;
                      const lineSavings = Math.max(0, regLineTotal - dealLineTotal);
                      const pctOff = reg > price ? Math.round(((reg - price) / reg) * 100) : 0;
                      return (
                        <div key={idx} className="flex justify-between text-sm">
                          <div className="flex-1 pr-2">
                            <span className="text-gray-700">{item.name} x{qty}</span>
                            {pctOff > 0 && (
                              <span
                                className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700"
                                data-testid={`item-discount-${order.id}-${idx}`}
                              >
                                {pctOff}% OFF
                              </span>
                            )}
                          </div>
                          <div className="text-right whitespace-nowrap">
                            {reg > price && (
                              <span className="text-gray-400 line-through mr-2">
                                ${regLineTotal.toFixed(2)}
                              </span>
                            )}
                            <span className="font-medium">${dealLineTotal.toFixed(2)}</span>
                            {lineSavings > 0 && (
                              <div className="text-[11px] text-emerald-600 font-semibold">
                                saved ${lineSavings.toFixed(2)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Totals */}
                  <div className="border-t pt-3 space-y-1 text-sm">
                    {(() => {
                      const totalSavings = order.items.reduce((acc, it) => {
                        const price = Number(it.price) || 0;
                        const qty = Number(it.quantity) || 0;
                        const reg = (Number(it.regular_price) || 0) * qty;
                        const deal = price * qty;
                        return acc + Math.max(0, reg - deal);
                      }, 0);
                      return totalSavings > 0 ? (
                        <div
                          className="flex justify-between text-emerald-700 font-semibold"
                          data-testid={`order-savings-${order.id}`}
                        >
                          <span>You Saved</span>
                          <span>−${totalSavings.toFixed(2)}</span>
                        </div>
                      ) : null;
                    })()}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal</span>
                      <span>${(order.subtotal || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Tax
                        {order.tax_breakdown && Object.keys(order.tax_breakdown).length > 0 && (
                          <span className="ml-1 text-xs text-gray-400">
                            ({Object.keys(order.tax_breakdown).map((b) => {
                              const labels = {
                                grocery: 'Groceries 1%',
                                personal_hygiene: 'Hygiene 1%',
                                prepared_food: 'Prepared 6%',
                                merchandise: 'Merchandise 6%',
                                exempt: 'Exempt',
                              };
                              return labels[b] || b;
                            }).join(' + ')})
                          </span>
                        )}
                      </span>
                      <span>${(order.tax || 0).toFixed(2)}</span>
                    </div>
                    {(order.delivery_fee || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Delivery Fee</span>
                        <span>${(order.delivery_fee || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {((order.charity_dac || 0) + (order.charity_drlp || 0) + (order.charity_roundup || 0)) > 0 && (
                      <div className="flex justify-between text-emerald-600">
                        <span>Charity Contributions</span>
                        <span>${((order.charity_dac || 0) + (order.charity_drlp || 0) + (order.charity_roundup || 0)).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold pt-2 border-t">
                      <span>Total</span>
                      <span data-testid={`order-total-${order.id}`}>${(order.total || 0).toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Environmental Impact — small footprint card under Total.
                      Conservative defaults (USDA / EPA):
                        - meals rescued = round(savings / $5)
                        - CO2e saved    = item_count * 0.8 kg
                        - water saved   = item_count * 25 gal */}
                  {(() => {
                    const totalSavings = order.items.reduce((acc, it) => {
                      const price = Number(it.price) || 0;
                      const qty = Number(it.quantity) || 0;
                      const reg = (Number(it.regular_price) || 0) * qty;
                      const deal = price * qty;
                      return acc + Math.max(0, reg - deal);
                    }, 0);
                    const itemUnits = order.items.reduce((acc, it) => acc + (Number(it.quantity) || 0), 0);
                    if (totalSavings <= 0 && itemUnits <= 0) return null;
                    const meals = Math.max(1, Math.round(totalSavings / 5));
                    const co2 = (itemUnits * 0.8).toFixed(1);
                    const water = Math.round(itemUnits * 25);
                    return (
                      <div
                        className="mt-3 rounded-xl p-3 bg-emerald-50/60 border border-emerald-100 text-xs text-emerald-900"
                        data-testid={`order-impact-${order.id}`}
                      >
                        <p className="font-semibold text-emerald-800 mb-1">🌱 Your impact from this order</p>
                        <div className="flex flex-wrap gap-3">
                          <span><strong>{meals}</strong> meal{meals > 1 ? 's' : ''} rescued</span>
                          <span>·</span>
                          <span>~<strong>{co2}</strong> kg CO₂e saved</span>
                          <span>·</span>
                          <span>~<strong>{water}</strong> gal water saved</span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Marketplace Facilitator disclosure — required on receipts
                      by VA Code § 58.1-612.1 (we collect & remit the tax). */}
                  <p
                    className="mt-3 text-[10px] text-gray-400 leading-snug"
                    data-testid={`mpf-disclosure-${order.id}`}
                  >
                    Sales tax collected and remitted by DealShaq Technologies Inc. as a
                    registered Virginia Marketplace Facilitator (Cert #10-992929133F-001).
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Charity Impact Summary — empty-state CTA when consumer hasn't picked a charity yet */}
        {orders.length > 0 && (() => {
          const totalCharity = orders.reduce(
            (sum, o) => sum + (o.charity_dac || 0) + (o.charity_drlp || 0) + (o.charity_roundup || 0),
            0
          );
          if (totalCharity > 0) {
            return (
              <Card
                className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200"
                data-testid="charity-impact-card"
              >
                <CardHeader>
                  <CardTitle className="text-emerald-800">Your Charity Impact</CardTitle>
                  <CardDescription>Total contributions through your orders</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-emerald-600">
                    ${totalCharity.toFixed(2)}
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Thank you for making a difference!</p>
                </CardContent>
              </Card>
            );
          }
          // Empty state — encourage charity selection with local NoVA options.
          return (
            <Card
              className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200"
              data-testid="charity-impact-card-empty"
            >
              <CardHeader>
                <CardTitle className="text-emerald-800">Pick a local charity to support</CardTitle>
                <CardDescription>
                  Every DealShaq order automatically donates 0.45% to your chosen charity — at no
                  extra cost to you. Choose one below to start your impact.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {[
                  { name: 'Capital Area Food Bank', focus: 'Distributes 50M+ meals/year across DC, MD & VA', url: 'https://www.capitalareafoodbank.org/' },
                  { name: 'Cornerstones (Reston)', focus: 'Hunger, housing & emergency aid in Northern Virginia', url: 'https://www.cornerstonesva.org/' },
                  { name: 'Food For Others', focus: 'Free groceries for working families in Fairfax County', url: 'https://foodforothers.org/' },
                  { name: 'Loudoun Hunger Relief', focus: 'Local food pantry serving Loudoun County families', url: 'https://www.loudounhunger.org/' },
                ].map((c) => (
                  <a
                    key={c.name}
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-lg bg-white border border-emerald-200 px-4 py-3 hover:border-emerald-400 hover:shadow-sm transition"
                    data-testid={`charity-cta-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <p className="text-sm font-semibold text-emerald-800">{c.name}</p>
                    <p className="text-xs text-gray-600">{c.focus}</p>
                  </a>
                ))}
                <Button
                  variant="outline"
                  className="w-full mt-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => navigate('/consumer/settings')}
                  data-testid="charity-pick-button"
                >
                  Set my charity preference →
                </Button>
              </CardContent>
            </Card>
          );
        })()}
      </div>
    </ConsumerLayout>
  );
}
